import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Scissors } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { label: "Meet Reek", hash: "#about" },
  { label: "Services", hash: "#services" },
  { label: "Work", hash: "#gallery" },
  { label: "Reviews", hash: "#reviews" },
  { label: "Visit", hash: "#visit" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToHash = (hash) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + hash);
      return;
    }
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/70 border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <Scissors className="w-5 h-5 text-[#d4af37] transition-transform duration-500 group-hover:rotate-90" strokeWidth={1.5} />
          <span className="font-display text-lg tracking-wide text-white">
            The Barbers <span className="italic text-[#d4af37]">Chair</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.label}
              onClick={() => goToHash(l.hash)}
              className="overline text-xs text-white/70 hover:text-[#d4af37] transition-colors duration-300"
              data-testid={`nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          {user && user.role === "admin" && (
            <Link to="/admin" className="overline text-xs text-white/70 hover:text-[#d4af37] transition-colors" data-testid="nav-admin">
              Dashboard
            </Link>
          )}
          {user ? (
            <>
              {user.role !== "admin" && (
                <Link to="/account" className="overline text-xs text-white/70 hover:text-[#d4af37] transition-colors" data-testid="nav-account">
                  My Bookings
                </Link>
              )}
              <button onClick={() => { logout(); navigate("/"); }} className="overline text-xs text-white/50 hover:text-white transition-colors" data-testid="nav-logout">
                Logout
              </button>
            </>
          ) : (
            <Link to="/login" className="overline text-xs text-white/70 hover:text-[#d4af37] transition-colors" data-testid="nav-login">
              Sign In
            </Link>
          )}
          <Link to="/gift-card" className="overline text-xs text-white/70 hover:text-[#d4af37] transition-colors" data-testid="nav-gift-card">
            Gift Cards
          </Link>
          <Link
            to="/book"
            className="group inline-flex items-center gap-2 border border-[#d4af37] px-6 py-2.5 overline text-xs text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors duration-300"
            data-testid="nav-book-btn"
          >
            Book Your Appointment
          </Link>
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-black/95 border-t border-white/10"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) => (
                <button key={l.label} onClick={() => goToHash(l.hash)} className="overline text-sm text-white/80 text-left">
                  {l.label}
                </button>
              ))}
              {user && user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="overline text-sm text-white/80">Dashboard</Link>}
              {user && user.role !== "admin" && <Link to="/account" onClick={() => setOpen(false)} className="overline text-sm text-white/80">My Bookings</Link>}
              {user ? (
                <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="overline text-sm text-white/50 text-left">Logout</button>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="overline text-sm text-white/80">Sign In</Link>
              )}
              <Link to="/gift-card" onClick={() => setOpen(false)} className="overline text-sm text-white/80" data-testid="nav-gift-card-mobile">Gift Cards</Link>
              <Link to="/book" onClick={() => setOpen(false)} className="border border-[#d4af37] px-6 py-3 overline text-xs text-[#d4af37] text-center">Book Now</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
